import {
  AddPhotoAlternate,
  Check,
  Delete,
  Error,
  RotateLeft,
  RotateRight,
  Save,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EmployeeProfileDto } from '~/services/Http/client.people.generated';

import {
  ProfilePictureDialogStatus,
  ProfilePictureDialogStatusMessage,
  useEditProfilePicture,
} from './useEditProfilePicture';
import { useSxStyles } from './useStyles';

type PropsType = {
  employeeProfile?: EmployeeProfileDto;
  onClose: () => void;
  onSubmitSuccess: () => void;
};

export const UserProfilePictureDialogContent = (props: PropsType) => {
  const { onClose, onSubmitSuccess, employeeProfile } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { cropper, picture, statusMessage } = useEditProfilePicture({
    employeeProfile,
    onSubmitSuccess,
  });

  const ProfilePictureStatusMessage = ({
    statusMessage,
  }: {
    statusMessage?: ProfilePictureDialogStatusMessage;
  }) => {
    const getStateProps = (
      status?: ProfilePictureDialogStatus,
    ): { color?: string; icon?: ReactNode } => {
      switch (status) {
        case ProfilePictureDialogStatus.Error:
          return {
            color: 'error.main',
            icon: <Error fontSize={'inherit'} />,
          };
        case ProfilePictureDialogStatus.Success:
          return {
            color: 'success.main',
            icon: <Check fontSize={'inherit'} />,
          };
        case ProfilePictureDialogStatus.Loading:
          return { icon: <CircularProgress size={12} /> };
        default:
          return {};
      }
    };

    const stateProps = getStateProps(statusMessage?.status);

    return !!statusMessage ? (
      <Typography color={stateProps.color}>
        <Box
          component={'span'}
          sx={{
            display: 'inline-block',
            verticalAlign: 'middle',
            marginRight: 0.5,
          }}
        >
          {!!stateProps.icon && stateProps.icon}
        </Box>
        {statusMessage?.message}
      </Typography>
    ) : null;
  };

  return (
    <>
      <DialogContent dividers={true}>
        <Box sx={sxStyles.imgLayout}>
          <Box sx={sxStyles.imgCropper}>
            <Box className={'cropFrame'}>
              <Box className={'cropContainer'}>
                <img
                  className={'cropImage'}
                  onLoad={cropper.onInit}
                  ref={cropper.ref}
                  src={cropper.imgUrl}
                  alt={t('laPicture')}
                />
              </Box>
            </Box>
            <Box className={'cropActions'}>
              <Button
                startIcon={<ZoomIn />}
                onClick={() => cropper.actions.zoomImage(0.1)}
              >
                {t('laZoomIn')}
              </Button>
              <Button
                startIcon={<ZoomOut />}
                onClick={() => cropper.actions.zoomImage(-0.1)}
              >
                {t('laZoomOut')}
              </Button>
              <Button
                startIcon={<RotateLeft />}
                onClick={() => cropper.actions.rotateImage(-90)}
              >
                {t('laRotateLeft')}
              </Button>
              <Button
                startIcon={<RotateRight />}
                onClick={() => cropper.actions.rotateImage(90)}
              >
                {t('laRotateRight')}
              </Button>
            </Box>
          </Box>

          <Box sx={sxStyles.imgUpload}>
            <Button
              startIcon={<AddPhotoAlternate />}
              onClick={() => picture.actions.onSelectFile()}
            >
              {t('laChoosePicture')}
            </Button>

            <input
              ref={picture.inputFileRef}
              accept={'image/*'}
              type={'file'}
              onChange={picture.actions.onUpload}
            />
          </Box>

          <Box
            sx={sxStyles.imgPreview}
            className={cropper.previewClassName}
          ></Box>

          <Box sx={sxStyles.imgActions}>
            <Button
              startIcon={<Save />}
              onClick={() => picture.actions.onSave()}
            >
              {t('laSave')}
            </Button>
            <Button
              startIcon={<Delete />}
              onClick={() => picture.actions.onRemove()}
              color={'error'}
            >
              {t('laDelete')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={sxStyles.dialogFooter}>
        <Box className={'statusMessage'}>
          <ProfilePictureStatusMessage statusMessage={statusMessage} />
        </Box>
        <Box className={'buttons'}>
          <Button onClick={onClose}>{t('close')}</Button>
        </Box>
      </DialogActions>
    </>
  );
};
